import { useEffect, useRef } from "react";
import type {
  BulkActionRequest,
  TeamAppProps,
} from "./teamAppZoid";

// AHA-41755 bulk-action bridge — host → embed.
//
// Subscribes to zoid's `xprops.onProps` (the same mechanism the i18n
// language bridge uses) and invokes `handler` once per *new* request, keyed
// on `nonce`. Re-renders with the same nonce are ignored, so the handler
// can safely run side effects (API calls, toasts) without guarding for
// double-fire.
//
// `handler` is captured in a ref so callers don't need to wrap it in
// useCallback — the subscription stays stable across renders.

type ZoidXProps = TeamAppProps & {
  onProps?: (
    handler: (props: TeamAppProps) => void,
  ) => { cancel: () => void };
};

// Mirrors the I18n bridge's polling window — zoid usually finishes its
// handshake within tens of ms, but the React tree can mount before
// `window.xprops` is populated.
const POLL_INTERVAL_MS = 50;
const TIMEOUT_MS = 1500;

export function useHostBulkActionListener(
  handler: (request: BulkActionRequest) => void,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let sub: { cancel: () => void } | undefined;
    const deadline = Date.now() + TIMEOUT_MS;
    let lastNonce: string | null = null;

    const apply = (props: TeamAppProps) => {
      const req = props.bulkActionRequest;
      if (!req || !req.nonce) return;
      if (req.nonce === lastNonce) return;
      lastNonce = req.nonce;
      handlerRef.current(req);
    };

    const setup = () => {
      if (cancelled) return;
      const xprops = (window as { xprops?: ZoidXProps }).xprops;
      if (!xprops) {
        if (Date.now() >= deadline) return;
        timer = setTimeout(setup, POLL_INTERVAL_MS);
        return;
      }
      apply(xprops);
      sub = xprops.onProps?.((p: TeamAppProps) => apply(p));
    };
    setup();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      sub?.cancel();
    };
  }, []);
}
